// Profile page (read-only overview) - no hero per requirements
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/useAuth";
import { getMyCompanyApplication } from "@/lib/adminApi";
import type { CompanyApplication } from "@/types/admin";

import { Card, CardHeader, CardTitle, CardContent } from "@/lib/components/ui/card";
import { Badge } from "@/lib/components/ui/badge";
import { Skeleton } from "@/lib/components/ui/skeleton";
import { Button } from "@/lib/components/ui/button";
import { Avatar, AvatarFallback } from "@/lib/components/ui/avatar";
import {
  ShoppingBag,
  Building2,
  Settings,
  Copy,
  Check,
  Package,
  Boxes,
  Users,
  FileText,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { formatMemberSince } from "@/utils/formatting";

type CompanyStatus = "processing" | "approved" | "rejected" | "revoked" | "withdrawn" | null;

// Status badge helper using translations
function getStatusBadge(status: CompanyStatus, t: TFunction) {
  switch (status) {
    case "processing":
      return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">{t('status.pending')}</Badge>;
    case "approved":
      return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">{t('status.approved')}</Badge>;
    case "rejected":
      return <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">{t('status.rejected')}</Badge>;
    case "revoked":
      return <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400">{t('status.revoked')}</Badge>;
    case "withdrawn":
      return <Badge className="bg-gray-100 text-gray-800 dark:bg-gray-700/30 dark:text-gray-400">{t('status.withdrawn')}</Badge>;
    default:
      return <Badge className="bg-gray-100 text-gray-800 dark:bg-gray-700/30 dark:text-gray-400">{t('status.notApplied')}</Badge>;
  }
}

// Remove the old formatMemberSince function as we're using the one from utils

// Get initial for avatar
function getInitial(username: string): string {
  return username ? username.charAt(0).toUpperCase() : "?";
}

// Loading skeleton
// --- START: Updated skeleton width (max-w-6xl -> max-w-5xl) to reduce empty space ---
function ProfileSkeleton() {
  return (
    <div className="transition-colors">
      {/* Content skeleton */}
      <section className="pb-16">
        <div className="max-w-5xl mx-auto space-y-6">
          {/* Overview card skeleton */}
          <Card className="bg-card">
            <CardContent className="py-6">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <Skeleton className="h-20 w-20 rounded-full" />
                <div className="flex-1 space-y-3 text-center md:text-left">
                  <Skeleton className="h-7 w-40 mx-auto md:mx-0" />
                  <Skeleton className="h-5 w-56 mx-auto md:mx-0" />
                  <div className="flex gap-2 justify-center md:justify-start">
                    <Skeleton className="h-6 w-20" />
                    <Skeleton className="h-6 w-20" />
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 mt-6 justify-center md:justify-start">
                <Skeleton className="h-9 w-28" />
                <Skeleton className="h-9 w-36" />
                <Skeleton className="h-9 w-28" />
              </div>
            </CardContent>
          </Card>

          {/* Two column skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-card">
              <CardHeader>
                <Skeleton className="h-6 w-36" />
              </CardHeader>
              <CardContent className="space-y-4">
                <Skeleton className="h-5 w-full" />
                <Skeleton className="h-5 w-full" />
                <Skeleton className="h-5 w-full" />
              </CardContent>
            </Card>
            <Card className="bg-card">
              <CardHeader>
                <Skeleton className="h-6 w-36" />
              </CardHeader>
              <CardContent className="space-y-4">
                <Skeleton className="h-5 w-full" />
                <Skeleton className="h-5 w-full" />
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
// --- END: Updated skeleton width (max-w-6xl -> max-w-5xl) to reduce empty space ---

const Profile = () => {
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation('profile');
  const [companyData, setCompanyData] = useState<CompanyApplication | null>(null);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);

  const isAdmin = user?.is_admin ?? false;
  const companyStatus = (user?.company_review_status ?? null) as CompanyStatus;
  const isApproved = companyStatus === "approved";
  const isPending = companyStatus === "processing";
  const hasApplied = companyStatus !== null;

  // Fetch company data for approved users
  useEffect(() => {
    if (isApproved) {
      setCompanyLoading(true);
      getMyCompanyApplication()
        .then((data) => setCompanyData(data))
        .catch((error) => {
          // Gracefully handle missing company data
          console.error("Failed to fetch company data:", error);
          setCompanyData(null);
        })
        .finally(() => setCompanyLoading(false));
    }
  }, [isApproved]);

  // Copy email to clipboard
  const handleCopyEmail = async () => {
    if (!user?.email) return;
    try {
      await navigator.clipboard.writeText(user.email);
      setCopiedEmail(true);
      toast.success(t('emailCopied'));
      setTimeout(() => setCopiedEmail(false), 2000);
    } catch (error) {
      console.error("Failed to copy email:", error);
      if (error instanceof Error && error.name === "NotAllowedError") {
        toast.error(t('clipboardDenied'));
      } else {
        toast.error(t('emailCopyFailed'));
      }
    }
  };

  // Show skeleton while auth is loading
  if (authLoading) {
    return <ProfileSkeleton />;
  }

  // Handle case where user is not logged in
  if (!user) {
    return (
      <div className="flex items-center justify-center py-16">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center">{t('notLoggedIn')}</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-muted-foreground mb-4">
              {t('mustBeLoggedIn')}
            </p>
            <Button asChild>
              <Link to="/login">{t('logIn')}</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="transition-colors">
      {/* --- START: Narrower content width (max-w-6xl -> max-w-5xl) to reduce empty space --- */}
      {/* Content section */}
      <section className="pb-16">
        <div className="max-w-5xl mx-auto space-y-6">
          {/* Overview Card (full width) */}
          <Card className="bg-card">
            <CardContent className="py-6">
              <div className="flex flex-col md:flex-row items-center gap-6">
                {/* Avatar with initial */}
                <Avatar className="h-20 w-20 text-2xl font-bold bg-primary text-primary-foreground">
                  <AvatarFallback className="bg-primary text-primary-foreground text-2xl font-bold">
                    {getInitial(user.username)}
                  </AvatarFallback>
                </Avatar>

                {/* User info */}
                <div className="flex-1 text-center md:text-left">
                  <h2 className="text-xl font-semibold">{user.username}</h2>
                  <p className="text-muted-foreground">{user.email}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('memberSince')} {formatMemberSince(user.created_at)}
                  </p>

                  {/* Badges */}
                  <div className="flex flex-wrap gap-2 mt-3 justify-center md:justify-start">
                    {getStatusBadge(companyStatus, t)}
                    {isAdmin && (
                      <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400">
                        Admin
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions row */}
              <div className="flex flex-wrap gap-3 mt-6 justify-center md:justify-start">
                {/* View Orders (hide for admins) */}
                {!isAdmin && (
                  <Button asChild variant="outline" size="sm">
                    <Link to="/account/orders">
                      <ShoppingBag className="h-4 w-4 mr-2" />
                      {t('actions.viewOrders')}
                    </Link>
                  </Button>
                )}

                {/* Company CTA based on status */}
                {!isAdmin && (
                  <>
                    {!hasApplied && (
                      <Button asChild variant="outline" size="sm">
                        <Link to="/account/settings?tab=company">
                          <Building2 className="h-4 w-4 mr-2" />
                          {t('actions.applyAsCompany')}
                        </Link>
                      </Button>
                    )}
                    {hasApplied && !isApproved && (
                      <Button asChild variant="outline" size="sm">
                        <Link to="/account/company">
                          <FileText className="h-4 w-4 mr-2" />
                          {t('actions.viewApplication')}
                        </Link>
                      </Button>
                    )}
                    {isApproved && (
                      <Button asChild variant="outline" size="sm">
                        <Link to="/account/company">
                          <Building2 className="h-4 w-4 mr-2" />
                          {t('actions.companyDashboard')}
                        </Link>
                      </Button>
                    )}
                  </>
                )}

                {/* Edit Settings */}
                <Button asChild variant="outline" size="sm">
                  <Link to="/account/settings">
                    <Settings className="h-4 w-4 mr-2" />
                    {t('actions.editSettings')}
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Two column layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Personal Details Card (left column) */}
            <Card className="bg-card">
              <CardHeader>
                <CardTitle className="text-lg">{t('sections.personalDetails')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-sm text-muted-foreground">{t('fields.username')}</span>
                  <span className="font-medium">{user.username}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-sm text-muted-foreground">{t('fields.email')}</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{user.email}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={handleCopyEmail}
                      title={t('actions.copyEmail')}
                      aria-label={t('actions.copyEmail')}
                    >
                      {copiedEmail ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-sm text-muted-foreground">{t('fields.memberSince')}</span>
                  <span className="font-medium">{formatMemberSince(user.created_at)}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-4">
                  {t('messages.updateDetails')}{" "}
                  <Link to="/account/settings" className="text-primary hover:underline">
                    {t('messages.settings')}
                  </Link>
                  .
                </p>
              </CardContent>
            </Card>

            {/* Right column: Company Summary / Admin Shortcuts / Application Status / Apply Card */}
            {isAdmin ? (
              /* Admin Shortcuts Card */
              <Card className="bg-card">
                <CardHeader>
                  <CardTitle className="text-lg">{t('sections.adminShortcuts')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Link
                    to="/admin/orders"
                    className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Package className="h-5 w-5 text-muted-foreground" />
                      <span className="font-medium">Orders</span>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </Link>
                  <Link
                    to="/admin/inventory"
                    className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Boxes className="h-5 w-5 text-muted-foreground" />
                      <span className="font-medium">Inventory</span>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </Link>
                  <Link
                    to="/admin/clients"
                    className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Users className="h-5 w-5 text-muted-foreground" />
                      <span className="font-medium">Clients</span>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </Link>
                </CardContent>
              </Card>
            ) : isApproved ? (
              /* Company Summary Card (approved users) */
              <Card className="bg-card">
                <CardHeader>
                  <CardTitle className="text-lg">{t('sections.companySummary')}</CardTitle>
                </CardHeader>
                <CardContent>
                  {companyLoading ? (
                    <div className="space-y-3">
                      <Skeleton className="h-5 w-full" />
                      <Skeleton className="h-5 w-3/4" />
                      <Skeleton className="h-5 w-1/2" />
                    </div>
                  ) : companyData ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-muted-foreground">{t('fields.companyName')}</p>
                          <p className="font-medium">{companyData.company_name}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">{t('fields.taxId')}</p>
                          <p className="font-medium">{companyData.tax_id}</p>
                        </div>
                        {companyData.email && (
                          <div>
                            <p className="text-xs text-muted-foreground">{t('fields.billingEmail')}</p>
                            <p className="font-medium">{companyData.email}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-xs text-muted-foreground">{t('fields.invoicePayments')}</p>
                          <p className="font-medium">
                            {companyData.invoice_enabled ? t('values.enabled') : t('values.disabled')}
                          </p>
                        </div>
                      </div>
                      <Button asChild variant="outline" className="w-full mt-4">
                        <Link to="/account/company">
                          <Building2 className="h-4 w-4 mr-2" />
                          {t('actions.goToCompany')}
                        </Link>
                      </Button>
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      <p>{t('messages.companyInfoUnavailable')}</p>
                      <Button asChild variant="outline" className="mt-4">
                        <Link to="/account/company">{t('actions.goToCompany')}</Link>
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : isPending ? (
              /* Application Status Card (pending users) */
              <Card className="bg-card">
                <CardHeader>
                  <CardTitle className="text-lg">{t('sections.applicationStatus')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3 mb-4">
                    <FileText className="h-8 w-8 text-yellow-500" />
                    <div>
                      <p className="font-medium">{t('messages.applicationUnderReview')}</p>
                      <p className="text-sm text-muted-foreground">
                        {t('messages.responseTime')}
                      </p>
                    </div>
                  </div>
                  <div className="mb-4">
                    {getStatusBadge(companyStatus, t)}
                  </div>
                  <Button asChild variant="outline" className="w-full">
                    <Link to="/account/company">
                      <FileText className="h-4 w-4 mr-2" />
                      {t('actions.viewApplication')}
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ) : hasApplied ? (
              /* Rejected/Revoked/Withdrawn status card */
              <Card className="bg-card">
                <CardHeader>
                  <CardTitle className="text-lg">{t('sections.companyStatus')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    {getStatusBadge(companyStatus, t)}
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    {companyStatus === "rejected" && t('messages.rejectedMessage')}
                    {companyStatus === "revoked" && t('messages.revokedMessage')}
                    {companyStatus === "withdrawn" && t('messages.withdrawnMessage')}
                  </p>
                  <Button asChild variant="outline" className="w-full">
                    <Link to="/account/company">
                      <Building2 className="h-4 w-4 mr-2" />
                      {companyStatus === "rejected" || companyStatus === "withdrawn"
                        ? t('messages.reapply')
                        : t('messages.viewDetails')}
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ) : (
              /* Apply as Company Card (not applied) */
              <Card className="bg-card">
                <CardHeader>
                  <CardTitle className="text-lg">{t('sections.companyAccount')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start gap-3 mb-4">
                    <Building2 className="h-8 w-8 text-primary shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">{t('messages.unlockFeatures')}</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {t('messages.featuresDescription')}
                      </p>
                    </div>
                  </div>
                  <Button asChild className="w-full">
                    <Link to="/account/settings?tab=company">
                      <Building2 className="h-4 w-4 mr-2" />
                      {t('actions.applyAsCompany')}
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>
      {/* --- END: Narrower content width (max-w-6xl -> max-w-5xl) to reduce empty space --- */}
    </div>
  );
};

export default Profile;